import { type DataTableColumn } from "@/components/ui/DataTable/index";
import { formatNumberToDisplay } from "@/lib/api/common";
import { ClientOpenPosition } from "@/lib/api/risk/summary";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { CommodityOptions } from "@/lib/api/commodity";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader, Trash2 } from "lucide-react";
import { ContractTypeOptions } from "@/lib/api/otc";

export function getPositionColumns<T extends ClientOpenPosition>({ disableActions, loadingRows, handleDelete }: { disableActions: boolean; loadingRows?: Record<string, 'delete'>; handleDelete: any }) {
    const columns: DataTableColumn<ClientOpenPosition>[] =  [
        /* {
*     id: "is_manually_inserted",
*     accessorKey: "is_manually_inserted",
*     header: "Manual",
*     accessorFn: (row: any) => {
*         if (/rjo/img.test(row.source_type)) { return "No"; }
*         if (/otc/img.test(row.contract_type)) { return "No"; }
*         return "Yes";
*     },
* }, */
        {
            id: "actions",
            header: "Actions",
            cell: ({ row }) => {
                const rowId = row.original.id;
                const loadingState = loadingRows?.[rowId];
                const isAnyLoading = !!loadingState;
                const isAutomatedDirectSale = row.original.is_automated ?? false;
                const isAutomatedRow = isAutomatedDirectSale;
                if (disableActions || !row.original.is_manually_inserted || isAutomatedRow) {
                    return (
                        <div className="flex gap-2 flex-row items-center">
                            --
                        </div>
                    );
                }

                return (
                    <div className="flex gap-2 flex-row items-center">
                        <Button
                            size="icon"
                            variant="outline"
                            onClick={() => handleDelete([rowId])}
                            disabled={isAnyLoading}
                            className="h-6 w-6"
                        >
                            {loadingState === 'delete' ? <Loader className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                    </div>
                );
            },
            size: 100,
            disableRowClick: true,
            enableSorting: false,
            enableFiltering: false,
        } as DataTableColumn<ClientOpenPosition>,
        {
            id: "type",
            accessorKey: 'type',
            header: "Type",
                accessorFn: (row: any) => {
                    if (row.detailed_contract_type) {
                        const item_type = row.detailed_contract_type;
                        return ContractTypeOptions.find(item => item.value === item_type as any)?.label || item_type;
                    }
                    if (row.original_otc) {
                        const item_type = row.original_otc.contract_type;
                        return ContractTypeOptions.find(item => item.value === item_type as any)?.label || item_type;
                    }
                    if (/open order.*rjo/im.test(row.source_type || "")) { return "Open Order - RJO"; }
                    if (/open order.*tt/im.test(row.source_type || "")) { return "Open Order - TT"; }
                    if (/reown/img.test(row.original_row?.contract_type || "")) {
                        const item_type = row.original_row?.contract_type;
                        return ContractTypeOptions.find(item => item.value === item_type as any)?.label || item_type;
                    }
                    if (/otc/img.test(row.contract_type || "")) { return "OTC"; }
                    if (/rjo/img.test(row.source_type)) { return "RJO"; }
                    return row.source_type;
                },
        },
        {
            id: "instrument_type",
            accessorKey: "instrument_type",
            accessorFn: (row: any) => {
                if (row.instrument_type) {
                    if (/open order.*tt/im.test(row.source_type || "") && /future/i.test(row.instrument_type)) { return "HTA"; }
                    return row.instrument_type;
                }
                if (!!row.put_or_call) { return 'O'; }
                if (row.price != "0") { return 'px'; }
                return "F";
            },
            header: "Instrument",
            enableSorting: false,
            enableFiltering: false,
            size: 100,
        },
        {
            id: "contract_number",
            accessorFn: (row: any) => row.contract_number  || row.contract_no|| row?.original_row?.contract_number ||  "",
            accessorKey: "contract_number",
            header: "Contract #"
        },
        {
            id: "customer_id",
            accessorKey: "customer_id",
            header: "Customer",
                accessorFn: (row: any) => {
                    return row.Customer?.long_name || ""
                },
        },
        {
            id: "commodity",
            accessorKey: "commodity",
            header: "Commodity",
            accessorFn: (row: any) => {
                return CommodityOptions.find((option: { value: string }) => option.value === row.commodity)?.label || row.commodity;
            },
        },
        {
            id: "crop_year",
            accessorKey: "crop_year",
            header: "Crop Year",
        },
        {
            id: "option_month",
            accessorKey: "option_month",
            header: "Option Month"
        },
        {
            id: "put_or_call",
            accessorKey: "put_or_call",
            header: "P/C",
        },
        {
            id: "long_qty",
            accessorKey: "long_qty",
            header: "Long Qty",
        },
        {
            id: "short_qty",
            accessorKey: "short_qty",
            header: "Short Qty",
        },
        {
            id: "bushel",
            accessorKey: "bushel",
            header: "Quantity",
            accessorFn: (row: any) => {
                return formatNumberToDisplay(row.bushel) || formatNumberToDisplay(row.quantity)
            },
        },
        {
            id: "note",
            accessorKey: "note",
            header: "Note",
            minSize: 100,
            enableTooltip: true,
            accessorFn: (row: any) => {
                return row.note || ""
            },
        },
        {
            id: "futures",
            accessorKey: "futures",
            header: "Futures",
                accessorFn: (row: any) => {
                    return formatNumberToDisplay(row.futures)
                },
        },
        {
            id: "basis",
            accessorKey: "basis",
            header: "Basis",
                accessorFn: (row: any) => {
                    return formatNumberToDisplay(row.basis)
                },
        },
        {
            id: "price",
            accessorKey: "price",
            header: "Price",
                accessorFn: (row: any) => {
                    return formatNumberToDisplay(row.price)
                },
        },
        {
            id: "contract_release",
            accessorKey: "contract_release",
            header: "Rel",
            accessorFn: (row: any) => {
                return row.original_row?.contract_release || ""
            },
        },
        {
            id: "trading_account",
            accessorKey: "trading_account",
            header: "Account #",
        },
        {
            id: "created_at",
            accessorKey: "created_at",
            header: "Created At",
            cell: (info) => moment(info.getValue() as string).format("MM/DD/YYYY HH:mm"),
        } as DataTableColumn<ClientOpenPosition>,
        {
            id: "contract_description",
            accessorKey: "contract_description",
            header: "Contract Description",
        },
        {
            id: "symbol_code",
            accessorKey: "symbol_code",
            header: "Symbol Code",
        },

    ].filter(Boolean);

    return columns;
}
